import React, { useState, useEffect, useCallback, useRef } from "react";
import OrdersPanel from "./OrdersPanel";
import ImagesPanel from "./ImagesPanel";
import { getAuth } from "firebase/auth";
import { getDoc, doc, collection, getDocs, writeBatch, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "../firebase"; // adjust path as needed
import { refreshOrderPaymentStatus as refreshOrderPaymentStatusCallable } from "../utils/firebaseFunctions";
import UsersPanel from "./UsersPanel"; // Import UsersPanel if needed
import CompaniesPanel from "./CompaniesPanel";
import InvoicesPanel from "./InvoicesPanel";
import DeveloperPanel from "./DeveloperPanel";
import "./AdminOrders.scss";
import { IoChevronDown, IoChevronForward, IoHammer, IoHammerOutline } from "react-icons/io5";
import {
  HiOutlineHome,
  HiOutlineShoppingBag,
  HiOutlineUsers,
  HiOutlineBuildingOffice2,
  HiOutlineDocumentText,
  HiMiniHome,
  HiMiniShoppingBag,
  HiMiniUsers,
  HiMiniBuildingOffice2,
  HiMiniDocumentText,
} from "react-icons/hi2";
import ShirtlabPanel from "./shirtlabPanel";

export { getSupabaseClient, fetchSupabaseTable } from "../utils/supabaseClient";

// thinner, rounded strokes
const IconDashboard = ({ active }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      d="M3 11h8V3H3v8zm10 10h8v-8h-8v8zM3 21h8v-6H3v6zm10-10h8V3h-8v8z"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconOrders = ({ active }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      d="M7 3h10l1 3h2v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6h2l1-3z"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 11h8M8 15h6"
      stroke={active ? "#fff" : "currentColor"}
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={active ? 0.9 : 0}
    />
  </svg>
);

// fixed React attrs + thinner stroke + even/centered icon
const IconUsers = ({ active }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    {/* Head */}
    <circle
      cx="12"
      cy="8"
      r="3"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={1}
    />
    {/* Body */}
    <path
      d="M5 19v-1a5 5 0 0 1 10 0v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1Z"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconInvoices = ({ active }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      d="M7 3h7l4 4v14H7z"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 12h6M9 16h6"
      stroke={active ? "#fff" : "currentColor"}
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Simple inline SVG line chart (no external deps) ---

function MiniSpark({ data = [], color = '#94c940', width = 120, height = 48, padding = 6 }) {
  const W = width, H = height, P = padding;
  const n = data.length || 0;
  const max = Math.max(1, ...data);
  const min = 0;
  const dx = n > 1 ? (W - 2 * P) / (n - 1) : 0;
  const sx = (i) => P + i * dx;
  const sy = (v) => {
    const t = (v - min) / (max - min);
    return H - P - t * (H - 2 * P);
  };
  const path = data.reduce((acc, v, i) => acc + (i ? ' L ' : 'M ') + sx(i) + ',' + sy(v), '');
  const area = data.length ? `${path} L ${sx(n - 1)},${H - P} L ${sx(0)},${H - P} Z` : '';
  const baselineY = H - P - (H - 2 * P) * 0.18; // dashed near bottom like screenshot
  return (
    <svg className="ao-spark" viewBox={`0 0 ${W} ${H}`} style={{ '--spark': color }}>
      <path d={`M ${P},${baselineY} H ${W - P}`} className="ao-spark__baseline" />

      <path d={path} className="ao-spark__glow" />
      <path d={path} className="ao-spark__line" />

    </svg>
  );
}

function StatCard({ title, value, deltaPct, trend = 'flat', series = [], color = '#94c940' }) {
  const DirIcon = () => <Arrow dir={trend} />;
  const pctTxt = Number.isFinite(deltaPct) ? `${(Math.round(deltaPct * 10) / 10).toFixed(1)}%` : '—';
  const trendCls = trend === 'up' ? 'up' : trend === 'down' ? 'down' : '';
  return (
    <div className="ao-statcard">
      <div>
        <div className="ao-statcard__title">{title}</div>
        <div className="ao-statcard__valueWrap">
          <div className="ao-statcard__value">{value}</div>
          <div className="ao-statcard__sep">-</div>
        </div>
        <div className="ao-statcard__delta"><span className={trendCls}><DirIcon /></span> {pctTxt} last week</div>
      </div>
      <MiniSpark data={series} color={color} />
    </div>
  );
}
function LineChart({ data = [], width = 600, height = 120, padding = 8, showDots = true }) {
  const W = width, H = height, P = padding;
  const n = data.length || 0;
  const max = Math.max(1, ...data);
  const min = 0; // start at 0 for counts
  const dx = n > 1 ? (W - 2 * P) / (n - 1) : 0;
  const scaleX = (i) => P + i * dx;
  const scaleY = (v) => {
    const t = (v - min) / (max - min);
    return H - P - t * (H - 2 * P);
  };

  const path = data.reduce((acc, v, i) => {
    const x = scaleX(i);
    const y = scaleY(v);
    return acc + (i === 0 ? `M ${x},${y}` : ` L ${x},${y}`);
  }, '');

  // simple under-area for a nicer look
  const area = data.length
    ? `${path} L ${scaleX(n - 1)},${H - P} L ${scaleX(0)},${H - P} Z`
    : '';

  return (
    <svg className="ao-linechart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Orders trend">
      <defs>
        {/* soft green glow for line */}
        <filter id="ao-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

      </defs>
      <path d={area} className="ao-linechart__area" />
      {/* blurred/stroked path for glow underlay */}
      <path d={path} className="ao-linechart__glow" filter="url(#ao-glow)" />
      {/* crisp line on top */}
      <path d={path} className="ao-linechart__path" />
      {showDots && data.map((v, i) => {
        const x = scaleX(i);
        const y = scaleY(v);
        return (
          <g key={i}>
            {/* soft glow halo */}
            <circle className="ao-linechart__dotGlow" cx={x} cy={y} r={5} filter="url(#ao-dot-glow)" />
            {/* crisp dot on top */}
            <circle className="ao-linechart__dot" cx={x} cy={y} />
          </g>
        );
      })}
    </svg>
  );
}

function Arrow({ dir = 'up' }) {
  const rot = dir === 'down' ? 'rotate(180 6 6)' : dir === 'flat' ? 'rotate(90 6 6)' : '';
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
      <g transform={rot}>
        <path d="M6 2 L10 7 H7 V10 H5 V7 H2 Z" fill="currentColor" />
      </g>
    </svg>
  );
}

function TrendCard({ title, value, deltaPct, trend = 'flat' }) {
  const cls = trend === 'up' ? 'ao-trend-delta--up' : trend === 'down' ? 'ao-trend-delta--down' : 'ao-trend-delta--flat';
  const fmt = (n) => (Number.isFinite(n) ? `${n > 0 ? '+' : ''}${(Math.round(n * 10) / 10).toFixed(1)}%` : '—');
  return (
    <div className="ao-trend-card">
      <div className="ao-trend-card__title">{title}</div>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
        <div className="ao-trend-card__value">{value}</div>
        <div className={`ao-trend-card__delta ${cls}`}>
          <Arrow dir={trend} /> {fmt(deltaPct)}
        </div>
      </div>

    </div>
  );
}

function DashboardHome() {
  const [orders, setOrders] = React.useState([]);
  const [artOrders, setArtOrders] = React.useState([]);
  const [artUserDetails, setArtUserDetails] = React.useState({});
  const [userDetails, setUserDetails] = React.useState({});
  const [trend, setTrend] = React.useState({
    weeklySeries: [],
    dailySeries: [],
    weeklyRevSeries: [],
    dailyRevSeries: [],
    weeklyRevSeries: [],
    dailyRevSeries: [],
    dailyItemsSeries: [],
    dailyCompletedSeries: [],
    dailyReturnSeries: [],
    metrics: { thisWeek: 0, lastWeek: 0, avgWeek4: 0, avgDay7: 0, weekDeltaPct: 0 },
    metricsRev: { thisWeek: 0, lastWeek: 0, avgWeek4: 0, avgDay7: 0, weekDeltaPct: 0 },
    extra: {
      items7: 0, itemsPrev7: 0, itemsDeltaPct: 0,
      completed7: 0, completedPrev7: 0, completedDeltaPct: 0,
      returns7: 0, returnsPrev7: 0, returnsDeltaPct: 0,
    }
  });

  React.useEffect(() => {
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(4));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const recent = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(recent);

      const fetchUserDetails = async (ordersList) => {
        const newUserDetails = {};
        const missingCustomerIds = ordersList
          .map(order => order.customerId)
          .filter((id, i, arr) => !newUserDetails[id] && arr.indexOf(id) === i);

        const userFetches = missingCustomerIds.map(async (id) => {
          const docRef = doc(db, "users", id);
          const docSnap = await getDoc(docRef);
          return {
            id,
            data: docSnap.exists() ? docSnap.data() : { firstName: "Loading", lastName: "" }
          };
        });

        Promise.all(userFetches).then((results) => {
          results.forEach(({ id, data }) => {
            newUserDetails[id] = data;
          });
          setUserDetails(newUserDetails);
        });
      };

      fetchUserDetails(recent);
    });

    return () => unsubscribe();
  }, []);

  React.useEffect(() => {
    const q = query(collection(db, "artOrders"), orderBy("createdAt", "desc"), limit(4));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const recent = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setArtOrders(recent);

      const fetchArtUserDetails = async (ordersList) => {
        const newUserDetails = {};
        const missingCustomerIds = ordersList
          .map(order => order.customerId)
          .filter((id, i, arr) => !newUserDetails[id] && arr.indexOf(id) === i);

        const userFetches = missingCustomerIds.map(async (id) => {
          const docRef = doc(db, "users", id);
          const docSnap = await getDoc(docRef);
          return {
            id,
            data: docSnap.exists() ? docSnap.data() : { firstName: "Loading", lastName: "" }
          };
        });

        Promise.all(userFetches).then((results) => {
          results.forEach(({ id, data }) => {
            newUserDetails[id] = data;
          });
          setArtUserDetails(newUserDetails);
        });
      };

      fetchArtUserDetails(recent);
    });

    return () => unsubscribe();
  }, []);

  // --- helpers to avoid optional chaining (for older mobile Safari) ---
  const getOrg = (user) => {
    if (!user) return null;
    const fb = user.freshbooksClientId;
    return fb && typeof fb === 'object' && fb.organization ? fb.organization : null;
  };
  const getFullName = (user) => {
    if (!user) return null;
    const first = typeof user.firstName === 'string' ? user.firstName : '';
    const last = typeof user.lastName === 'string' ? user.lastName : '';
    const name = `${first} ${last}`.trim();
    return name || null;
  };
  const getInitials = (name) => {
    if (!name) return '??';
    const parts = String(name).split(' ').filter(Boolean);
    const a = parts[0] ? parts[0][0].toUpperCase() : '';
    const b = parts.length > 1 ? parts[parts.length - 1][0].toUpperCase() : '';
    return (a + b) || a || 'A';
  };
  const toDateSafe = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') { try { return ts.toDate(); } catch (e) { } }
    if (typeof ts === 'number') return new Date(ts);
    if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
    return null;
  };
  const fmtDate = (ts) => {
    const d = toDateSafe(ts);
    return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
  };
  const getLocationLabel = (order) => {
    if (!order) return '-';
    if (order.location) return String(order.location);
    if (order.pickupLocation) return String(order.pickupLocation);
    if (order.city) return String(order.city);
    return '-';
  };
  const getItemsCount = (order) => {
    if (!order) return null;
    if (Array.isArray(order.items)) return order.items.length;
    if (Array.isArray(order.images)) return order.images.length;
    return null;
  };
  const getSheetSize = (order) => {
    if (!order) return null;
    const w = order.sheetWidthIn || order.sheetWidth || order.sheetWidthMm;
    const h = order.sheetHeightIn || order.sheetHeight || order.sheetHeightMm;
    if (w && h) {
      const hasIn = !!(order.sheetWidthIn || order.sheetHeightIn);
      return `${w}×${h}${hasIn ? ' in' : ''}`;
    }
    return null;
  };

  const fmtMoney = (n) => (Number(n) || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  const getOrderTotal = (o) => {
    const v = o?.total ?? o?.totalAmount ?? o?.amount ?? o?.price ?? o?.payment?.total ?? 0;
    const num = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(num) ? num : 0;
  };
  const getItemCount = (o) => Array.isArray(o?.items) ? o.items.length : Array.isArray(o?.images) ? o.images.length : 0;
  const getPictureQuantityCount = (o) => {
    const sumQuantities = (arr) =>
      arr.reduce((acc, entry) => {
        const qty = Number(entry?.quantity);
        return acc + (Number.isFinite(qty) && qty > 0 ? qty : 1);
      }, 0);
    if (Array.isArray(o?.images)) return sumQuantities(o.images);
    if (Array.isArray(o?.items)) return sumQuantities(o.items);
    return 0;
  };
  const isCompleted = (o) => String(o?.status || '').toLowerCase() === 'completed';
  const isReturn = (o) => {
    const s = String(o?.status || '').toLowerCase();
    return s.includes('return') || s.includes('refund') || s.includes('cancel');
  };

  // --- Trend helpers ---
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const startOfWeek = (d) => { const x = startOfDay(d); const day = x.getDay(); x.setDate(x.getDate() - day); return x; }; // Sunday-start weeks

  const buildDailySeries = (orders, days = 30) => {
    const today = startOfDay(new Date());
    const keys = Array.from({ length: days }).map((_, i) => {
      const d = new Date(today); d.setDate(d.getDate() - (days - 1 - i));
      return d.toISOString().slice(0, 10);
    });
    const map = Object.create(null);
    for (const o of orders) {
      const d = toDateSafe(o.createdAt);
      if (!d) continue;
      const k = startOfDay(d).toISOString().slice(0, 10);
      map[k] = (map[k] || 0) + 1;
    }
    return keys.map(k => map[k] || 0);
  };

  const buildWeeklySeries = (orders, weeks = 8) => {
    const end = startOfWeek(new Date());
    const starts = Array.from({ length: weeks }).map((_, i) => {
      const d = new Date(end); d.setDate(d.getDate() - (weeks - 1 - i) * 7); return d;
    });
    const counts = starts.map(() => 0);

    for (const o of orders) {
      const d = toDateSafe(o.createdAt);
      if (!d) continue;
      const t = +startOfDay(d);
      for (let i = 0; i < starts.length; i++) {
        const s = +starts[i];
        const e = s + 7 * 24 * 60 * 60 * 1000;
        if (t >= s && t < e) { counts[i] += 1; break; }
      }
    }
    return counts;
  };

  const buildDailySeriesSum = (orders, days = 30, valueFn = getOrderTotal) => {
    const today = startOfDay(new Date());
    const keys = Array.from({ length: days }).map((_, i) => {
      const d = new Date(today); d.setDate(d.getDate() - (days - 1 - i));
      return d.toISOString().slice(0, 10);
    });
    const map = Object.create(null);
    for (const o of orders) {
      const d = toDateSafe(o.createdAt);
      if (!d) continue;
      const k = startOfDay(d).toISOString().slice(0, 10);
      map[k] = (map[k] || 0) + (valueFn(o) || 0);
    }
    return keys.map(k => map[k] || 0);
  };

  const buildWeeklySeriesSum = (orders, weeks = 8, valueFn = getOrderTotal) => {
    const end = startOfWeek(new Date());
    const starts = Array.from({ length: weeks }).map((_, i) => {
      const d = new Date(end); d.setDate(d.getDate() - (weeks - 1 - i) * 7); return d;
    });
    const sums = starts.map(() => 0);
    for (const o of orders) {
      const d = toDateSafe(o.createdAt);
      if (!d) continue;
      const t = +startOfDay(d);
      for (let i = 0; i < starts.length; i++) {
        const s = +starts[i];
        const e = s + 7 * 24 * 60 * 60 * 1000;
        if (t >= s && t < e) { sums[i] += (valueFn(o) || 0); break; }
      }
    }
    return sums;
  };

  const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
  const pct = (curr, prev) => {
    if (!Number.isFinite(prev) || prev === 0) return curr > 0 ? 100 : 0;
    return ((curr - prev) / prev) * 100;
  };
  // Subscribe to recent orders for order trends (8w/30d rolling)
  React.useEffect(() => {
    const since = new Date();
    since.setDate(since.getDate() - 90);
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(500));
    const unsub = onSnapshot(q, (snapshot) => {
      const rows = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const filtered = rows.filter(o => {
        const d = toDateSafe(o.createdAt);
        return d && d >= since;
      });
      const daily = buildDailySeries(filtered, 30);
      const weekly = buildWeeklySeries(filtered, 8);
      const sum = (arr) => arr.reduce((a, b) => a + b, 0);
      const this7 = sum(daily.slice(-7));
      const prev7 = sum(daily.slice(-14, -7));
      const weekDeltaPct = pct(this7, prev7);
      const avgWeek4 = avg(weekly.slice(-4));
      const avgDay7 = avg(daily.slice(-7));

      // revenue
      const dailyRev = buildDailySeriesSum(filtered, 30);
      const weeklyRev = buildWeeklySeriesSum(filtered, 8);
      const this7Rev = sum(dailyRev.slice(-7));
      const prev7Rev = sum(dailyRev.slice(-14, -7));
      const weekRevDeltaPct = pct(this7Rev, prev7Rev);
      const avgWeek4Rev = avg(weeklyRev.slice(-4));
      const avgDay7Rev = avg(dailyRev.slice(-7));

      // items
      const dailyItems = buildDailySeriesSum(filtered, 30, getPictureQuantityCount);
      const items7 = sum(dailyItems.slice(-7));
      const itemsPrev7 = sum(dailyItems.slice(-14, -7));
      const itemsDeltaPct = pct(items7, itemsPrev7);

      // completed orders
      const completed = filtered.filter(isCompleted);
      const dailyCompleted = buildDailySeries(completed, 30);
      const completed7 = sum(dailyCompleted.slice(-7));
      const completedPrev7 = sum(dailyCompleted.slice(-14, -7));
      const completedDeltaPct = pct(completed7, completedPrev7);

      // returns / cancelled (approx.)
      const returned = filtered.filter(isReturn);
      const dailyReturn = buildDailySeries(returned, 30);
      const returns7 = sum(dailyReturn.slice(-7));
      const returnsPrev7 = sum(dailyReturn.slice(-14, -7));
      const returnsDeltaPct = pct(returns7, returnsPrev7);

      setTrend({
        weeklySeries: weekly,
        dailySeries: daily,
        weeklyRevSeries: weeklyRev,
        dailyRevSeries: dailyRev,
        dailyItemsSeries: dailyItems,
        dailyCompletedSeries: dailyCompleted,
        dailyReturnSeries: dailyReturn,
        metrics: { thisWeek: this7, lastWeek: prev7, avgWeek4, avgDay7, weekDeltaPct },
        metricsRev: { thisWeek: this7Rev, lastWeek: prev7Rev, avgWeek4: avgWeek4Rev, avgDay7: avgDay7Rev, weekDeltaPct: weekRevDeltaPct },
        extra: { items7, itemsPrev7, itemsDeltaPct, completed7, completedPrev7, completedDeltaPct, returns7, returnsPrev7, returnsDeltaPct }
      });
    });
    return () => unsub();
  }, []);


  const SHOW_ART = false;

  return (
    <div className="ao-page">
      <div className="ao-shell">
        <div className="ao-grid">
          <section className="ao-statgrid">
            <StatCard
              title="Total Orders"
              value={trend.metrics.thisWeek}
              deltaPct={trend.metrics.weekDeltaPct}
              trend={trend.metrics.weekDeltaPct > 0 ? 'up' : trend.metrics.weekDeltaPct < 0 ? 'down' : 'flat'}
              series={trend.dailySeries.slice(-20)}
              color="#f59e0b"
            />
            <StatCard
              title="Order items over time"
              value={trend.extra.items7}
              deltaPct={trend.extra.itemsDeltaPct}
              trend={trend.extra.itemsDeltaPct > 0 ? 'up' : trend.extra.itemsDeltaPct < 0 ? 'down' : 'flat'}
              series={trend.dailyItemsSeries.slice(-20)}
              color="#10b981"
            />
            <StatCard
              title="Fulfilled orders over time"
              value={trend.extra.completed7}
              deltaPct={trend.extra.completedDeltaPct}
              trend={trend.extra.completedDeltaPct > 0 ? 'up' : trend.extra.completedDeltaPct < 0 ? 'down' : 'flat'}
              series={trend.dailyCompletedSeries.slice(-20)}
              color="#22c55e"
            />
          </section>


          <section className="ao-card ao-card--orders">
            <div className="ao-card__header">
              <div className="ao-card__title">DTF Orders</div>
              <div className="ao-pill">{orders.length} new</div>
            </div>
            <div className="ao-list">
              {orders.map((order) => {
                const user = userDetails && userDetails[order.customerId];
                const org = getOrg(user);
                const fullname = getFullName(user);
                const customerName = org || fullname || 'Loading';
                const status = order.status || 'Pending';
                const statusKey = String(status).toLowerCase().replace(/\s+/g, '-');
                const dateLabel = fmtDate(order && order.createdAt) || '—';
                const locationLabel = getLocationLabel(order);
                const itemsCount = getItemsCount(order);
                const sizeLabel = getSheetSize(order);
                const initials = getInitials(customerName);
                const avatarUrl =
                  user?.profileImageURL ||
                  user?.profileImageUrl ||
                  user?.photoURL ||
                  user?.avatarUrl ||
                  "";
                return (
                  <div key={order.id} className="ao-order">
                    <div className="ao-order__avatar">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="" loading="lazy" />
                      ) : (
                        initials
                      )}
                    </div>
                    <div className="ao-order__meta">
                      <div className="ao-order__name">{customerName}</div>
                      <div className="ao-order__line">
                        Items: {itemsCount != null ? itemsCount : '—'}{sizeLabel ? ` • Size: ${sizeLabel}` : ''}
                      </div>
                      <div className="ao-order__sub">Order ID: {String(order.id).slice(0, 6)} • Created: {dateLabel}</div>
                    </div>
                    <div className="ao-order__right">
                      <span className={`ao-status ao-status--${statusKey}`}>{status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {SHOW_ART && (
            <section className="ao-card">
              <div className="ao-card__header">
                <div className="ao-card__title">Art Orders</div>
                <div className="ao-pill">{artOrders.length} new</div>
              </div>
              <div className="ao-list">
                {artOrders.map((order) => {
                  const user = artUserDetails && artUserDetails[order.customerId];
                  const org = getOrg(user);
                  const fullname = getFullName(user);
                  const customerName = org || fullname || 'Loading';
                  const status = order.status || 'Pending';
                  const statusKey = String(status).toLowerCase().replace(/\s+/g, '-');
                  return (
                    <div key={order.id} className="ao-item">
                      <div className="ao-item__left">
                        <span className="ao-item__name">{customerName}</span>
                        <span className="ao-item__id">Order ID: {String(order.id).slice(0, 6)}</span>
                      </div>
                      <span className={`ao-status ao-status--${statusKey}`}>{status}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [stackedOrders, setStackedOrders] = useState([]);
  const [selectedPage, setSelectedPage] = useState("admin");
  const [users, setUsers] = useState([]);
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [newArtOrderCount, setNewArtOrderCount] = useState(0);
  const [resetSelectedOrder, setResetSelectedOrder] = useState(false);
  const [siteBanner, setSiteBanner] = useState(null);

  const [lastKnownOrderTimestamp, setLastKnownOrderTimestamp] = useState(() => {
    return parseInt(localStorage.getItem("lastKnownOrderTimestamp") || "0", 10);
  });

  const [visibleOrderIds, setVisibleOrderIds] = useState([]);
  const lastVisibleSyncKeyRef = useRef('');

  const normalizeString = (value) =>
    typeof value === 'string' ? value.toLowerCase().trim() : '';

  const handleVisibleOrdersChange = useCallback((ids = []) => {
    setVisibleOrderIds((prev) => {
      if (prev.length === ids.length && prev.every((id, index) => id === ids[index])) {
        return prev;
      }
      return ids;
    });
  }, []);

  const callRefreshOrderPaymentStatus = useCallback(async (orderIds) => {
    return refreshOrderPaymentStatusCallable(orderIds);
  }, []);

  const syncFreshBooksStatusesForOrders = useCallback(async (ordersList) => {
    if (!Array.isArray(ordersList) || ordersList.length === 0) {
      console.log("🔎 No orders available for FreshBooks status evaluation");
      return null;
    }

    console.log(`🔎 Checking FreshBooks status for ${ordersList.length} orders`);
    const normalize = (value) =>
      typeof value === "string"
        ? value.toLowerCase().replace(/[^a-z0-9]/g, "")
        : "";

    const candidates = new Set();
    let consolidatedPending = 0;
    let net30Pending = 0;
    let otherPending = 0;
    let missingInvoiceCount = 0;
    let alreadyPaidCount = 0;

    for (const order of ordersList) {
      const invoiceCandidates = [
        order?.invoice?.invoiceId,
        order?.invoice?.id,
        order?.invoiceId,
      ];
      const resolvedInvoiceId = invoiceCandidates
        .map((candidate) => {
          if (candidate === null || candidate === undefined) return "";
          const value = String(candidate).trim();
          return value;
        })
        .find((value) => Boolean(value)) || null;

      const invoiceType = String(order?.invoice?.type || "").toLowerCase();
      const termsKey = normalize(order?.paymentTerms || order?.paymentType || "");
      const statusKey = normalize(order?.paymentStatus || "");

      if (statusKey === "paid") {
        console.log(`✅ Order ${order.id} already marked paid; skipping FreshBooks sync`);
        alreadyPaidCount += 1;
        continue;
      }

      if (!resolvedInvoiceId) {
        console.log(
          `↪️ Order ${order.id} has no FreshBooks invoice ID; marking as unsent.`
        );
        missingInvoiceCount += 1;
        otherPending += 1;
        candidates.add(order.id);
        continue;
      }

      candidates.add(order.id);
      if (invoiceType === "consolidated") {
        consolidatedPending += 1;
      } else if (termsKey === "net30" || termsKey === "netthirty") {
        net30Pending += 1;
      } else {
        otherPending += 1;
      }

      console.log(
        `📌 Queued FreshBooks sync for order ${order.id} (invoice ${resolvedInvoiceId}, status ${order?.paymentStatus || "unknown"})`
      );
    }

    if (missingInvoiceCount > 0) {
      console.log(
        `ℹ️ ${missingInvoiceCount} order(s) will be marked as 'unsent' due to missing FreshBooks invoice IDs.`
      );
    }

    if (alreadyPaidCount > 0) {
      console.log(`ℹ️ ${alreadyPaidCount} order(s) already marked paid were skipped.`);
    }

    if (candidates.size === 0) {
      console.log(
        "✅ No unpaid FreshBooks invoices need syncing after evaluation"
      );
      return null;
    }

    console.log(
      `🧾 Orders queued for FreshBooks sync — Consolidated: ${consolidatedPending}, Net 30: ${net30Pending}, Other: ${otherPending}`
    );

    const overrides = new Map();
    const orderIds = Array.from(candidates);
    const chunkSize = 20;

    for (let i = 0; i < orderIds.length; i += chunkSize) {
      const chunk = orderIds.slice(i, i + chunkSize);
      console.log("🔁 Syncing chunk with FreshBooks:", chunk);

      let payload;
      try {
        payload = await callRefreshOrderPaymentStatus(chunk);
      } catch (chunkError) {
        console.warn("⚠️ FreshBooks chunk sync failed:", chunkError, chunk);
        continue;
      }

      const results = Array.isArray(payload?.results) ? payload.results : [];

      if (!results.length) {
        console.log(
          "ℹ️ FreshBooks returned no payment updates for chunk:",
          chunk
        );
      }

      for (const result of results) {
        const orderId = result?.orderId;
        const paymentStatus = result?.paymentStatus;
        const invoiceForLog =
          result?.invoiceId ||
          result?.invoice_id ||
          result?.invoiceNumber ||
          '(unknown)';

        console.log(
          `📦 FreshBooks raw response for order ${orderId || '(unknown)'} (invoice ${invoiceForLog}):`,
          result
        );

        if (result?.error) {
          console.warn(
            `⚠️ FreshBooks returned an error for order ${orderId || '(unknown)'}`,
            result.error
          );
          continue;
        }

        if (!orderId || !paymentStatus) {
          continue;
        }

        console.log(`💾 FreshBooks returned status '${paymentStatus}' for order ${orderId}`);
        overrides.set(orderId, { paymentStatus });
      }
    }

    return overrides.size ? overrides : null;
  }, [callRefreshOrderPaymentStatus]);

  const setupOrdersPolling = async () => {
    try {
      const snapshot = await getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc")));
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setOrders(ordersData);

      let latestTimestamp = 0;
      const lastKnown = parseInt(localStorage.getItem("lastKnownOrderTimestamp") || "0", 10);
      const newOrders = [];

      for (const doc of snapshot.docs) {
        const data = doc.data();
        const createdAt = data.createdAt?.toMillis?.();
        if (createdAt > latestTimestamp) latestTimestamp = createdAt;
        if (createdAt && createdAt > lastKnown) {
          newOrders.push({ id: doc.id, ...data });
        }
      }

      if (
        newOrders.length > 0 &&
        typeof Notification !== "undefined" &&
        Notification.permission === "granted"
      ) {
        new Notification("🧾 New DTF Order", {
          body: `${newOrders.length} new order${newOrders.length > 1 ? "s" : ""} received!`,
          icon: "/order-icon.png",
        });
      }

      if (latestTimestamp > lastKnown) {
        localStorage.setItem("lastKnownOrderTimestamp", latestTimestamp.toString());
      }

      setNewOrderCount(newOrders.length);
      if (selectedPage === 'orders') {
        lastVisibleSyncKeyRef.current = '';
      }

    } catch (error) {
      console.error("❌ Failed to fetch orders:", error);
    }
  };

  useEffect(() => {
    if (selectedPage !== 'orders') {
      setVisibleOrderIds([]);
      lastVisibleSyncKeyRef.current = '';
    }
  }, [selectedPage]);

  useEffect(() => {
    if (selectedPage !== 'orders') {
      lastVisibleSyncKeyRef.current = '';
      return;
    }

    if (!visibleOrderIds.length) {
      return;
    }

    const subset = orders.filter((order) => visibleOrderIds.includes(order.id));

    if (!subset.length) {
      return;
    }

    const key = subset
      .map((order) => {
        const invoiceId =
          order?.invoice?.invoiceId ||
          order?.invoice?.id ||
          order?.invoiceId ||
          '';
        return `${order.id}:${invoiceId}`;
      })
      .sort()
      .join('|');

    if (!key || key === lastVisibleSyncKeyRef.current) {
      return;
    }

    lastVisibleSyncKeyRef.current = key;

    let cancelled = false;

    (async () => {
      try {
        const overrides = await syncFreshBooksStatusesForOrders(subset);
        if (!overrides || overrides.size === 0 || cancelled) return;

        setOrders((prev) => {
          let changed = false;
          const next = prev.map((order) => {
            const override = overrides.get(order.id);
            if (!override) return order;
            const prevStatus = normalizeString(order.paymentStatus);
            const nextStatus = normalizeString(override.paymentStatus);
            if (prevStatus === nextStatus) {
              return order;
            }
            changed = true;
            return {
              ...order,
              paymentStatus: override.paymentStatus,
            };
          });
          return changed ? next : prev;
        });
      } catch (err) {
        console.warn('⚠️ Failed to sync FreshBooks payment statuses for visible orders:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedPage, visibleOrderIds, orders, syncFreshBooksStatusesForOrders]);

  // Delete all orders function
  const deleteAllOrders = async () => {
    try {
      const snapshot = await getDocs(collection(db, "orders"));
      const batch = writeBatch(db);
      if (!window.confirm("Are you sure you want to delete ALL orders? This action cannot be undone.")) {
        return;
      }
      snapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      console.log("✅ All orders deleted.");
      setupOrdersPolling(); // Refresh the orders list
    } catch (error) {
      console.error("❌ Failed to delete all orders:", error);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setIsAuthorized(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const data = snap.data();
        if (data?.role === 1) {
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error("Failed to fetch user role:", error);
        setIsAuthorized(false);
      }
      // Set up orders polling after auth state is determined
      return () => {
        unsubscribe();
      };
    });

    return () => unsubscribe();
  }, []);

  const fetchArtOrderCount = useCallback(async () => {
    try {
      const snapshot = await getDocs(query(collection(db, "artOrders"), orderBy("createdAt", "desc")));
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      const lastVisit = parseInt(localStorage.getItem("lastVisitedArtOrdersPage") || "0", 10);
      const newOrders = ordersData.filter(order => {
        const createdAt = order.createdAt?.toMillis?.();
        return createdAt && createdAt > lastVisit;
      });
      setNewArtOrderCount(newOrders.length);
    } catch (error) {
      console.error("❌ Failed to fetch art orders:", error);
    }
  }, []);

  useEffect(() => {
    if (selectedPage === 'admin') {
      fetchArtOrderCount();
    }
  }, [selectedPage, fetchArtOrderCount]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const usersData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort users by last name (assuming name is "First Last")
        usersData.sort((a, b) => {
          const getLastName = name => (name || "").split(" ").slice(-1)[0].toLowerCase();
          return getLastName(a.name).localeCompare(getLastName(b.name));
        });
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
      }

    };
    fetchUsers()
  }, []);

  useEffect(() => {
    setupOrdersPolling();
  }, []);

  useEffect(() => {
    if (users.length === 0) return;

    const fetchStackedOrders = async () => {
      try {
        const snapshot = await getDocs(collection(db, "stackedOrders"));
        const stacked = snapshot.docs.map((docSnap) => {
          const orderData = docSnap.data();
          const userData = users.find((u) => u.id === docSnap.id) || null;

          const customerName =
            userData?.freshbooksClientId?.organization ||
            `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim() ||
            "Unnamed Customer";

          return {
            id: docSnap.id,
            customerName,
            ...orderData,
          };
        });

        console.log("📦 Fetched stacked orders:", stacked);
        setStackedOrders(stacked);
      } catch (error) {
        console.error("❌ Failed to fetch stacked orders:", error);
      }
    };

    fetchStackedOrders();
  }, [users]);

  // Request notification permission on mount
  useEffect(() => {
    if (typeof Notification === "undefined") return;
    try {
      if (Notification.permission !== "granted") {
        const maybePromise = Notification.requestPermission?.();
        if (maybePromise && typeof maybePromise.catch === "function") {
          maybePromise.catch(() => {});
        }
      }
    } catch (_) {
      // Ignore if Notifications aren't supported (common on mobile Safari).
    }
  }, []);

  useEffect(() => {
    if (resetSelectedOrder) {
      setResetSelectedOrder(false);
    }
  }, [resetSelectedOrder]);

  useEffect(() => {
    // Live banner that shows above the navbar (editable in Developer panel)
    const unsub = onSnapshot(
      doc(db, "adminSettings", "siteBanner"),
      (snap) => {
        setSiteBanner(snap.exists() ? snap.data() : null);
      },
      (err) => {
        console.error("🔔 Banner subscription failed:", err);
        if (err?.code === "permission-denied") {
          // Hide banner instead of throwing noisy errors in UI
          setSiteBanner(null);
        }
      }
    );
    return () => unsub();
  }, []);

  if (isAuthorized === false) {
    return (
      <h4 style={{ padding: "1rem" }}>Page Not Found</h4>
    );
  }

  if (isAuthorized === true) {
    return (
      <div className="admin-container">
        <div className="topbar" role="navigation" aria-label="Mobile Navigation">
          <div className="topbar__inner">
            <span className="topbar__label">Navigate</span>
            <select
              className="topbar__select"
              value={selectedPage}
              onChange={(e) => {
                const next = e.target.value;
                setSelectedPage(next);
                if (next === 'orders') { setupOrdersPolling(); setResetSelectedOrder(true); }
                if (next === 'artOrders') { localStorage.setItem('lastVisitedArtOrdersPage', Date.now().toString()); }
              }}
            >

              <option value="admin">Dashboard</option>
              <optgroup label="Orders">
                <option value="orders">Direct-To-Film{newOrderCount > 0 ? `(${newOrderCount} new)` : ''}</option>
                <option value="shirtlab">Direct-To-Garment{newOrderCount > 0 ? `(${newOrderCount} new)` : ''}</option>
                <option value="artOrders">Art {newArtOrderCount > 0 ? `(${newArtOrderCount} new)` : ''}</option>
              </optgroup>
              <optgroup label="Customers">
                <option value="clients">Clients</option>
                <option value="companies">Companies</option>
              </optgroup>
              <option value="invoices">Invoices</option>
              <option value="developer">Developer</option>
            </select>
          </div>
        </div>
        <nav className="sidebar">
          <div className="nav-list">
            <button
              className={`nav-item ${selectedPage === 'admin' ? 'active' : ''}`}
              onClick={() => setSelectedPage('admin')}
            >
              <span className="nav-icon">
                {selectedPage === 'admin' ? <HiMiniHome /> : <HiOutlineHome />}
              </span>
              <span>Dashboard</span>
            </button>
            {/* Accordion Orders Button */}
            <div className="nav-accordion">
              <button
                className={`nav-item ${['orders', 'shirtlab', 'artOrders'].includes(selectedPage) ? 'active' : ''}`}
                onClick={() => setSelectedPage(selectedPage === 'orders' || selectedPage === 'artOrders' ? '' : 'orders')}
                aria-expanded={['orders', 'shirtlab', 'artOrders'].includes(selectedPage)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="nav-icon">
                    {['orders', 'shirtlab', 'artOrders'].includes(selectedPage)
                      ? <HiMiniShoppingBag />
                      : <HiOutlineShoppingBag />}
                  </span>
                  <span>Orders</span>
                </span>
                <span style={{ marginLeft: 8 }}>
                  {['orders', 'shirtlab', 'artOrders'].includes(selectedPage) ? (
                    <IoChevronDown />
                  ) : (
                    <IoChevronForward />
                  )}
                </span>
              </button>
              {['orders', 'shirtlab', 'artOrders'].includes(selectedPage) && (
                <div className="nav-sublist">
                  <button
                    className={`nav-subitem ${selectedPage === 'orders' ? 'active' : ''}`}
                    onClick={() => { setSelectedPage('orders'); setupOrdersPolling(); setResetSelectedOrder(true); }}
                  >
                    Direct-To-Film
                    {newOrderCount > 0 && (
                      <span className="badge">{newOrderCount}</span>
                    )}
                  </button>
                  <button
                    className={`nav-subitem ${selectedPage === 'shirtlab' ? 'active' : ''}`}
                    onClick={() => { setSelectedPage('shirtlab'); setupOrdersPolling(); setResetSelectedOrder(true); }}
                  >
                    Custom Apparel
                    {newOrderCount > 0 && (
                      <span className="badge">{newOrderCount}</span>
                    )}
                  </button>
                  <button
                    className={`nav-subitem ${selectedPage === 'artOrders' ? 'active' : ''}`}
                    onClick={() => setSelectedPage('artOrders')}
                  >
                    Art Services
                    {newArtOrderCount > 0 && (
                      <span className="badge">{newArtOrderCount}</span>
                    )}
                  </button>
                </div>
              )}
            </div>
            <div className="nav-accordion">
              <button
                className={`nav-item ${['clients', 'companies'].includes(selectedPage) ? 'active' : ''}`}
                onClick={() => setSelectedPage(['clients', 'companies'].includes(selectedPage) ? '' : 'clients')}
                aria-expanded={['clients', 'companies'].includes(selectedPage)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="nav-icon">
                    {['clients', 'companies'].includes(selectedPage)
                      ? <HiMiniUsers />
                      : <HiOutlineUsers />}
                  </span>
                  <span>Customers</span>
                </span>
                <span style={{ marginLeft: 8 }}>
                  {['clients', 'companies'].includes(selectedPage) ? (
                    <IoChevronDown />
                  ) : (
                    <IoChevronForward />
                  )}
                </span>
              </button>
              {['clients', 'companies'].includes(selectedPage) && (
                <div className="nav-sublist">
                  <button
                    className={`nav-subitem ${selectedPage === 'clients' ? 'active' : ''}`}
                    onClick={() => { setSelectedPage('clients'); }}
                  >
                    Clients
                  </button>
                  <button
                    className={`nav-subitem ${selectedPage === 'companies' ? 'active' : ''}`}
                    onClick={() => setSelectedPage('companies')}
                  >
                    Companies
                  </button>
                </div>
              )}
            </div>
            <button
              className={`nav-item ${selectedPage === 'invoices' ? 'active' : ''}`}
              onClick={() => setSelectedPage('invoices')}
            >
              <span className="nav-icon">
                {selectedPage === 'invoices' ? <HiMiniDocumentText /> : <HiOutlineDocumentText />}
              </span>
              <span>Invoices</span>
            </button>
            <button
              className={`nav-item ${selectedPage === 'developer' ? 'active' : ''}`}
              onClick={() => setSelectedPage('developer')}
            >
              <span className="nav-icon">
                {selectedPage === 'developer' ? <IoHammer /> : <IoHammerOutline />}
              </span>
              <span>Developer</span>
            </button>
          </div>
        </nav>

        <div className="main-content">
          {selectedPage === 'admin' && <DashboardHome />}
          {selectedPage === 'orders' && (
            <OrdersPanel
              key="orders-panel"
              orders={orders}
              users={users}
              fetchOrders={setupOrdersPolling}
              resetSelectedOrder={resetSelectedOrder}
              onVisibleOrdersChange={handleVisibleOrdersChange}
            />
          )}
          {selectedPage === 'artOrders' && <ImagesPanel />}
          {selectedPage === 'shirtlab' && <ShirtlabPanel />}
          {selectedPage === 'users' && <UsersPanel />}
          {selectedPage === 'clients' && <UsersPanel />}
          {selectedPage === 'companies' && <CompaniesPanel />}
          {selectedPage === 'invoices' && (
            <InvoicesPanel users={users} stackedOrders={stackedOrders} orders={orders} />
          )}

          {selectedPage === 'developer' && <DeveloperPanel />}
        </div>
      </div>
    );
  }

  return null;
}
